/**
 * Whether this browser can broadcast (`R-HOST-10`).
 *
 * > *Broadcasting requires a current Chromium-based browser. The supported set is narrower than
 * > the customer application's, and is stated plainly to hosts rather than discovered on the
 * > night.*
 *
 * The last clause is why this is a module with named outcomes rather than an `if` inside a
 * component. A host opening the panel in Safari an hour before a show must be told *that* — not
 * shown a camera picker that fails, and not shown a working preview that cannot publish.
 *
 * ## Why an iPhone is its own answer and not just *not Chromium*
 *
 * **Apple requires every iOS browser to use WebKit**, so there is no Chromium on the platform at
 * all and installing Chrome changes nothing — its user agent says `CriOS`, the engine underneath
 * is Safari's. A host told *use Chrome or Edge* on an iPhone will install Chrome, get the same
 * refusal, and conclude the panel is broken. So the device is reported separately from the
 * browser, because the remedy is a different **device** rather than a different browser.
 *
 * It is worth knowing that this is stricter than the provider requires: AWS lists Safari and
 * Chrome on iOS as supported for publishing. `R-VID-6` is what rules them out anyway — *ingest
 * must survive a network transition without ending the show… this is a hard requirement* — and
 * the IVS web SDK's own known issues say Safari does not recover from a network switch without
 * reloading the page. A host walking from wifi onto mobile data is the case the requirement was
 * written for, and on iOS every browser is that Safari.
 *
 * Pure, and separated from every browser API it reports on, so the decision is testable:
 * `apps/host/test/support.test.ts` drives it with the user agent strings that matter.
 */

export type Support =
  | { ok: true }
  /** Not Chromium. Publishing may partly work and is not supported; say so plainly. */
  | { ok: false; reason: "browser" }
  /**
   * An iPhone or iPad, where *not Chromium* is a property of the device rather than of the
   * browser and no other browser will help. See the note above.
   */
  | { ok: false; reason: "apple-mobile" }
  /** Chromium-ish but without the APIs publishing needs — an old version, or an insecure origin. */
  | { ok: false; reason: "capabilities" };

export interface BrowserFacts {
  /** `navigator.userAgentData.brands`, where the browser provides it. The reliable signal. */
  brands: { brand: string }[] | undefined;
  /** `navigator.userAgentData.mobile`, where the browser provides it. */
  mobileHint: boolean | undefined;
  userAgent: string;
  /**
   * `navigator.maxTouchPoints`. The only way to tell an iPad from a Mac: iPad Safari has claimed
   * to be `Macintosh` since iPadOS 13, and a Mac reports no touch points.
   */
  maxTouchPoints?: number;
  /** `navigator.mediaDevices` — absent on an insecure origin, which is a real way to hit this. */
  hasMediaDevices: boolean;
  /** What the IVS SDK says about its own environment (`isSupported()`). */
  sdkSupported: boolean;
}

/**
 * Chromium, by brand where the browser says so and by user agent where it does not.
 *
 * `userAgentData.brands` is the signal to prefer: it names Chromium explicitly and is not part of
 * the string every browser has spent twenty years lying in. Safari and Firefox do not implement
 * it, so its absence sends us to the user agent, where the tell is `Chrome/` — which Firefox's
 * string does not contain at all, and Safari's does not either, however much else it borrows.
 *
 * Edge, Opera and Samsung Internet all pass, and that is correct rather than a leak: they are
 * Chromium, they carry the same WebRTC stack, and `R-HOST-10` names the engine and not the badge.
 */
function chromium(facts: BrowserFacts): boolean {
  if (facts.brands) {
    return facts.brands.some(
      (entry) => entry.brand === "Chromium" || entry.brand === "Google Chrome",
    );
  }
  return /Chrom(e|ium)\//.test(facts.userAgent);
}

/**
 * An iPhone or iPad, by the two signals between them that cover the platform.
 *
 * The user agent names the device on an iPhone and on an iPad in its default *mobile* mode. An
 * iPad asking for desktop sites — which is iPad Safari's own default on the larger models — says
 * `Macintosh` instead and is indistinguishable from a laptop by string alone, so the touch points
 * are what separate them: a Mac reports none.
 */
function appleMobile(facts: BrowserFacts): boolean {
  if (/iPhone|iPad|iPod/.test(facts.userAgent)) return true;
  return /Macintosh/.test(facts.userAgent) && (facts.maxTouchPoints ?? 0) > 1;
}

export function support(facts: BrowserFacts): Support {
  if (!chromium(facts)) {
    // The device before the browser, because on this one the browser is not the host's choice.
    return { ok: false, reason: appleMobile(facts) ? "apple-mobile" : "browser" };
  }
  if (!facts.hasMediaDevices || !facts.sdkSupported) {
    return { ok: false, reason: "capabilities" };
  }
  return { ok: true };
}

/**
 * Whether this is a phone or a tablet, which decides two things about publishing: how many
 * simulcast layers are worth encoding, and whether the screen needs holding awake.
 *
 * `userAgentData.mobile` is a boolean the browser volunteers and is the signal to prefer — Chromium
 * provides it, and it is the one part of user-agent reporting that was designed rather than
 * accumulated. The string test behind it exists for browsers that do not, and errs towards *not*
 * mobile: the cost of being wrong that way is a laptop encoding one layer fewer, where the other
 * way is a phone encoding three.
 */
export function isMobile(facts: BrowserFacts): boolean {
  if (facts.mobileHint !== undefined) return facts.mobileHint;
  return /Android|iPhone|iPad|iPod|Mobile Safari/.test(facts.userAgent);
}

/** The facts as this browser reports them. The one impure line, kept away from the decision. */
export function browserFacts(sdkSupported: boolean): BrowserFacts {
  const data = (
    navigator as Navigator & {
      userAgentData?: { brands?: { brand: string }[]; mobile?: boolean };
    }
  ).userAgentData;
  return {
    brands: data?.brands,
    mobileHint: data?.mobile,
    userAgent: navigator.userAgent,
    maxTouchPoints: navigator.maxTouchPoints,
    hasMediaDevices: typeof navigator.mediaDevices?.getUserMedia === "function",
    sdkSupported,
  };
}
